import React, { useMemo } from "react";
import { useD3LineChart } from "../../../hooks/useD3LineChart";
import { PerformanceMetric } from "../../../services/performanceMetricsService";
import { formatUtcDate } from "../../../utils/dateUtils";
import { formatSignedPercentage } from "../../../utils/numberUtils";
import { usePortfolioPerformance } from "./usePortfolioPerformance";

interface PortfolioPerformanceChartProps {
  performanceMetrics: PerformanceMetric[];
  createdAt: string;
  isDarkMode: boolean;
  showWatermark?: boolean;
  height?: number;
}

const TIME_FILTER_OPTIONS = [
  { value: "1 week", label: "1W" },
  { value: "1 month", label: "1M" },
  { value: "3 months", label: "3M" },
  { value: "6 months", label: "6M" },
  { value: "Year to date", label: "YTD" },
  { value: "1 year", label: "1Y" },
  { value: "All", label: "All" },
  { value: "Since inception", label: "Inception" },
];

const formatLocalDate = (dateString: string): string => {
  return formatUtcDate(dateString, { month: "long", day: "numeric", year: "numeric" });
};

const PortfolioPerformanceChart: React.FC<PortfolioPerformanceChartProps> = ({
  performanceMetrics,
  createdAt,
  isDarkMode,
  showWatermark = true,
  height = 420,
}) => {
  const {
    selectedPeriod,
    availableFilters,
    orderedFiltersForTable,
    chartData,
    timeframePerformance,
    formattedCreateDate,
    handleSelectPeriod,
  } = usePortfolioPerformance({
    performanceMetrics,
    createdAt,
  });

  const chartColor = "#14b8a6";

  // D3 Chart Configuration
  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    data: chartData,
    lines: [
      {
        dataKey: "value",
        stroke: chartColor,
        strokeWidth: 2,
        label: "Performance",
        showDots: false,
      },
    ],
    isDarkMode,
    height,
    margins: { top: 8, right: 44, bottom: 24, left: 12 },
    yAxisFormatter: (value: number) => `${value}%`,
    tooltipFormatter: (value: number) => formatSignedPercentage(value),
    referencePoint: formattedCreateDate
      ? { x: formattedCreateDate, color: isDarkMode ? "#9CA3AF" : "#6B7280", radius: 5 }
      : undefined,
    showLegend: false,
  });

  // Get latest metric date for display
  const latestMetricDate = useMemo(() => {
    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return null;
    const sorted = [...factorMetrics].sort(
      (a, b) => new Date(b.metricDate).getTime() - new Date(a.metricDate).getTime()
    );
    return sorted[0]?.metricDate;
  }, [performanceMetrics]);

  if (chartData.length <= 1) {
    return null;
  }

  return (
    <div className={`${isDarkMode ? "bg-gray-750" : "bg-gray-50"} rounded-lg p-4 sm:p-6`}>
      <div
        ref={containerRef}
        style={{ width: "100%", height, position: "relative", overflow: "visible" }}
      >
        <svg ref={svgRef} style={{ display: "block" }} />
        <div
          ref={tooltipRef}
          style={{
            position: "absolute",
            display: "none",
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
            borderRadius: "0.375rem",
            padding: "8px 12px",
            pointerEvents: "none",
            zIndex: 1000,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          }}
        />
        {/* Watermark (icon + wordmark) */}
        {showWatermark && (
          <div
            className="absolute left-2 flex items-center gap-x-[6px] pointer-events-none select-none"
            style={{ opacity: 0.14, bottom: 36 }}
          >
            <img src="/favicon.png" alt="Plutus" className="h-9 w-9" />
            <img src="/logo.png" alt="Plutus" className="h-7 w-auto" />
          </div>
        )}
      </div>

      {/* Time filters below chart (abbreviated) */}
      <div className="px-3 mt-0">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-medium">
          {TIME_FILTER_OPTIONS.filter(item => availableFilters.includes(item.value as any)).map(item => (
            <button
              key={item.value}
              onClick={() => handleSelectPeriod(item.value)}
              aria-pressed={selectedPeriod === item.value}
              className={`px-2.5 py-1 rounded-full transition-colors border focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
                selectedPeriod === item.value
                  ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                  : isDarkMode
                  ? "text-gray-300 hover:text-teal-400 hover:bg-gray-700 border-transparent"
                  : "text-gray-600 hover:text-teal-700 hover:bg-teal-50 border-transparent"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Inception date */}
      {formattedCreateDate && (
        <div className={`mt-2 text-center text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
          Inception: {formatLocalDate(formattedCreateDate)}
        </div>
      )}

      {/* Performance Stats by Timeframe */}
      <div className={`mt-6 pt-6 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {orderedFiltersForTable.map(filter => {
            const perf = timeframePerformance[filter];
            return (
              <div key={filter} className="text-center">
                <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-1`}>{filter}</p>
                <p
                  className={`text-base font-bold ${
                    perf !== null && perf !== undefined
                      ? perf >= 0
                        ? "text-teal-600"
                        : "text-red-600"
                      : isDarkMode
                      ? "text-gray-500"
                      : "text-gray-400"
                  }`}
                >
                  {perf !== null && perf !== undefined ? formatSignedPercentage(perf) : "-"}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PortfolioPerformanceChart;
